import { useMemo, useState } from 'react';
import type {
  FixedPriceImportColumn,
  FixedPriceImportExisting,
  FixedPriceImportField,
  FixedPriceImportRow,
  FixedPriceImportSheet,
} from '@/api/preis-tool/preis-tool.types';
import { DsAlert, DsButton, DsCombobox } from '@/modules/internal-v2/ds';
import { fmtDate, fmtEUR } from '../helpers';
import {
  FIELD_LABELS,
  FIELDS,
  fieldOfColumn,
  headerRowCandidates,
  MAX_PRICE_LIST_COLUMNS,
  MAX_PRICE_LIST_ROWS,
  ROW_STATUS,
  type PriceListSheetCells,
} from '../price-list-file';

const SKIP_TITLE = 'Der Artikel hat schon vier aktive Fixpreise anderer Lieferanten.';

function replaceTitle(existing: FixedPriceImportExisting): string {
  const kind = [existing.origin === 'MANUAL' && 'von Hand erfassten', existing.expired && 'abgelaufenen']
    .filter(Boolean)
    .join(', ');
  return (
    `${kind ? `Ersetzt einen ${kind} Fixpreis` : 'Ersetzt den bisherigen Fixpreis'} ` +
    `(${fmtEUR(existing.netPrice)} €, ${existing.expired ? 'abgelaufen am' : 'gültig bis'} ${fmtDate(existing.validTo)}).`
  );
}

/** What taking the row over does: new, replaces a fixed price (old → new price) or skipped. */
function OutcomeCell({ row }: { row: FixedPriceImportRow }) {
  if (row.outcome === 'new') {
    return (
      <span className="ds-pill pos" title="Legt einen neuen Fixpreis an.">
        neu
      </span>
    );
  }
  if (row.outcome === 'replace' && row.existing) {
    return (
      <span title={replaceTitle(row.existing)}>
        <span className="ds-pill info">ersetzt</span>{' '}
        <span className="pt-import-change">
          <span className="old">{fmtEUR(row.existing.netPrice)}</span>
          <span className="arrow">→</span>
          <span className="new">{fmtEUR(row.netPrice)}</span>
        </span>
      </span>
    );
  }
  if (row.outcome === 'skip_limit') {
    return (
      <span className="ds-pill warn" title={SKIP_TITLE}>
        übersprungen
      </span>
    );
  }
  return '—';
}

const FIELD_OPTIONS = [
  {
    options: [
      { value: '', label: '— nicht verwenden —' },
      ...FIELDS.map((field) => ({ value: field, label: FIELD_LABELS[field] })),
    ],
  },
];

/** How the column got its field: recognised by a column name, assigned by hand, or a name says "do not use". */
function OriginCell({ sheet, column }: { sheet: FixedPriceImportSheet; column: FixedPriceImportColumn }) {
  const chosen = fieldOfColumn(sheet.mapping, column.index);
  const suggested = fieldOfColumn(sheet.detected, column.index);
  if (chosen !== suggested) {
    return (
      <span className="ds-pill info" title="Von Hand zugeordnet">
        von Hand
      </span>
    );
  }
  if (chosen && column.match) {
    return (
      <span className="pt-import-origin" title={`Erkannt am Spaltennamen „${column.match.name}“`}>
        erkannt
      </span>
    );
  }
  if (column.match?.field === 'ignore') {
    return (
      <span
        className="pt-import-origin"
        title={`Der Spaltenname „${column.match.name}“ steht unter „nicht verwenden“`}
      >
        ignoriert
      </span>
    );
  }
  return null;
}

function SheetState({ sheet }: { sheet: FixedPriceImportSheet }) {
  if (!sheet.included) {
    if (sheet.problem === 'NO_ROWS') {
      return <DsAlert severity="info">Unter der Kopfzeile stehen keine Zeilen. Das Blatt wird nicht übernommen.</DsAlert>;
    }
    if (sheet.problem) {
      return (
        <DsAlert severity="info">
          Auf diesem Blatt wurde {sheet.problem === 'NO_PRICE_COLUMN' ? 'keine Preisspalte' : 'weder Artikelnummer noch EAN'}{' '}
          erkannt, es wird nicht übernommen. Enthält es doch Preise: Spalten unten zuordnen und „Blatt übernehmen“ anhaken.
        </DsAlert>
      );
    }
    return <DsAlert severity="info">Dieses Blatt wird nicht übernommen.</DsAlert>;
  }
  if (sheet.problem === 'NO_PRICE_COLUMN') {
    return (
      <DsAlert severity="warn">
        Keine Spalte ist als EK netto zugeordnet. Wähle unten die Spalte mit dem Einkaufspreis oder nimm das Blatt heraus.
      </DsAlert>
    );
  }
  if (sheet.problem === 'NO_KEY_COLUMN') {
    return (
      <DsAlert severity="warn">
        Weder Artikelnummer noch EAN ist zugeordnet. Wähle unten mindestens eine davon oder nimm das Blatt heraus.
      </DsAlert>
    );
  }
  if (sheet.problem === 'NO_ROWS') {
    return (
      <DsAlert severity="warn">
        Unter der Kopfzeile stehen keine Zeilen. Wähle eine andere Kopfzeile oder nimm das Blatt heraus.
      </DsAlert>
    );
  }
  return null;
}

const LOCAL_PROBLEM: Record<NonNullable<PriceListSheetCells['problem']>, string> = {
  EMPTY: 'Das Blatt ist leer.',
  TOO_MANY_ROWS: `Das Blatt hat mehr als ${MAX_PRICE_LIST_ROWS.toLocaleString('de-DE')} Zeilen und wird nicht gelesen.`,
  TOO_MANY_COLUMNS: `Das Blatt hat mehr als ${MAX_PRICE_LIST_COLUMNS} Spalten und wird nicht gelesen.`,
};

interface PriceListSheetPanelProps {
  cells: PriceListSheetCells;
  /** The preview of this sheet; `null` for a sheet that is not sent or while it is computed. */
  sheet: FixedPriceImportSheet | null;
  /** While a preview runs and after the import. */
  readOnly: boolean;
  /** The outcome per row belongs to the supplier in the field. */
  showOutcome: boolean;
  onHeaderRow: (rowNumber: number) => void;
  onInclude: (include: boolean) => void;
  onColumnField: (columnIndex: number, field: FixedPriceImportField | null) => void;
  onEditNames: () => void;
}

/**
 * One sheet of the uploaded file: whether it is imported, its header row,
 * which column is used as which field (every column with its header, a few
 * values and where its field comes from), and its rows with the article each
 * belongs to.
 */
export function PriceListSheetPanel({
  cells,
  sheet,
  readOnly,
  showOutcome,
  onHeaderRow,
  onInclude,
  onColumnField,
  onEditNames,
}: PriceListSheetPanelProps) {
  const [onlyProblems, setOnlyProblems] = useState(false);
  const headerOptions = useMemo(
    () => [
      {
        options: headerRowCandidates(cells.rows).map(({ rowNumber, text }) => ({
          value: String(rowNumber),
          label: `Zeile ${rowNumber}`,
          hint: text.length > 48 ? `${text.slice(0, 48)}…` : text,
        })),
      },
    ],
    [cells.rows],
  );

  if (cells.problem) return <DsAlert severity="info">{LOCAL_PROBLEM[cells.problem]}</DsAlert>;
  if (!sheet) return <div className="pt-fix-empty">Lese Blatt…</div>;

  const rows = onlyProblems ? sheet.rows.filter((r) => r.status !== 'matched') : sheet.rows;
  const hasQuantity = sheet.mapping.quantity != null;
  return (
    <div className="pt-import-sheet">
      <div className="pt-import-sheet-bar">
        <label className="ds-ck">
          <input
            type="checkbox"
            checked={sheet.included}
            onChange={(e) => onInclude(e.target.checked)}
            disabled={readOnly}
          />
          <span className="box" />
          Blatt übernehmen
        </label>
        <label className="pt-import-header-row">
          <span className="pt-label">Kopfzeile</span>
          <DsCombobox
            value={String(sheet.headerRow)}
            onChange={(value) => value && onHeaderRow(Number(value))}
            groups={headerOptions}
            aria-label="Kopfzeile"
            disabled={readOnly}
            listMinWidth={360}
          />
        </label>
        <span className="pt-import-sheet-spacer" />
        <DsButton size="small" variant="ghost" onClick={onEditNames} disabled={readOnly}>
          Spaltennamen…
        </DsButton>
      </div>

      <SheetState sheet={sheet} />

      <div className="pt-import-columns ds-scroll">
        <table className="pt-sales-table">
          <thead>
            <tr>
              <th>Spalte</th>
              <th>Überschrift in der Datei</th>
              <th>Beispielwerte</th>
              <th>Verwenden als</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {sheet.columns.map((column) => {
              const field = fieldOfColumn(sheet.mapping, column.index);
              return (
                <tr key={column.index} className={field ? undefined : 'unused'}>
                  <td className="mono">{column.letter}</td>
                  <td>{column.header || <span className="pt-fix-hint">ohne Überschrift</span>}</td>
                  <td className="mono samples" title={column.samples.join('\n')}>
                    {column.samples.join(' · ') || '—'}
                  </td>
                  <td className="field">
                    <DsCombobox
                      value={field ?? ''}
                      onChange={(value) => onColumnField(column.index, (value || null) as FixedPriceImportField | null)}
                      groups={FIELD_OPTIONS}
                      aria-label={`Spalte ${column.letter} verwenden als`}
                      disabled={readOnly}
                      listMinWidth={180}
                    />
                  </td>
                  <td>
                    <OriginCell sheet={sheet} column={column} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {!sheet.problem && (
        <>
          <div className="pt-import-summary">
            <span>
              <b>{sheet.rowCount}</b> Zeilen · <b>{sheet.matchedCount}</b> Treffer ·{' '}
              <b>{sheet.rowCount - sheet.matchedCount}</b> ohne Treffer
              {!sheet.included && ' · Blatt wird nicht übernommen'}
            </span>
            <button className={`ds-toggle-chip${onlyProblems ? ' on' : ''}`} onClick={() => setOnlyProblems(!onlyProblems)}>
              Nur Zeilen ohne Treffer
            </button>
          </div>
          <div className={`pt-import-rows ds-scroll${sheet.included ? '' : ' pt-offer-set-aside'}`}>
            <table className="pt-sales-table">
              <thead>
                <tr>
                  <th>Zeile</th>
                  <th>Artikelnummer</th>
                  <th>EAN</th>
                  <th>Bezeichnung</th>
                  <th className="num">EK netto</th>
                  {hasQuantity && <th className="num">Menge</th>}
                  <th>Zuordnung</th>
                  {showOutcome && sheet.included && <th>Wirkung</th>}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const status = ROW_STATUS[row.status];
                  return (
                    <tr key={row.rowNumber}>
                      <td className="mono">{row.rowNumber}</td>
                      <td className="mono">{row.itemNumber || '—'}</td>
                      <td className="mono">{row.ean || '—'}</td>
                      <td className="pt-import-desc" title={row.description}>
                        {row.description || '—'}
                      </td>
                      <td className="num mono">{fmtEUR(row.netPrice)}</td>
                      {hasQuantity && (
                        <td className="num mono">{row.quantity == null ? '—' : row.quantity.toLocaleString('de-DE')}</td>
                      )}
                      <td title={row.itemName ?? status.title}>
                        <span className={`ds-pill ${status.cls}`} title={status.title}>
                          {status.label}
                        </span>{' '}
                        <span className="mono">{row.itemId ?? ''}</span>
                      </td>
                      {showOutcome && sheet.included && (
                        <td>
                          <OutcomeCell row={row} />
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
